import React, { useEffect, useRef } from "react";

interface ConfirmationModalProps {
  isOpen: boolean;
  warning?: React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  warning,
  onConfirm,
  onCancel,
}) => {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    confirmRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        onClick={(e) => e.stopPropagation()}
        className="dark:bg-boxdark w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="border-stroke dark:border-strokedark border-b px-6 py-4">
          <h3
            id="confirm-modal-title"
            className="text-lg font-semibold text-black dark:text-white"
          >
            Потвърждение
          </h3>
        </div>

        {/* Body */}
        <div className="space-y-4 px-6 py-5">
          {warning && (
            <div
              role="alert"
              className="border-warning/40 bg-warning/10 flex items-start gap-3 rounded-lg border p-4"
            >
              <span className="bg-warning/20 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full">
                <i
                  className="fa fa-warning text-warning text-lg"
                  aria-hidden="true"
                />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold uppercase tracking-wide text-black dark:text-white">
                  Внимание
                </p>
                <p className="mt-0.5 text-base leading-snug text-black dark:text-white">
                  {warning}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-stroke dark:border-strokedark flex justify-end gap-3 border-t bg-black/[0.03] px-6 py-4 dark:bg-white/5">
          <button
            type="button"
            onClick={onCancel}
            className="border-stroke dark:border-strokedark rounded-lg border bg-white px-4 py-2 text-sm font-medium text-black transition hover:bg-black/5 dark:bg-transparent dark:text-white dark:hover:bg-white/10"
          >
            Отказ
          </button>
          <button
            type="button"
            ref={confirmRef}
            onClick={onConfirm}
            className="bg-primary focus:ring-primary/40 rounded-lg px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90 focus:outline-none focus:ring-2"
          >
            ОК
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
